/**
 * ConnectionCreationPanel - Modern wizard-style panel for creating new connections
 *
 * Uses manifest-driven forms and real OAuth flows.
 * Supports different connection types:
 * - API Connections (OAuth, API Key)
 * - Service Provider Connections (connection string)
 * - Agent Connections (Azure OpenAI, OpenAI)
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Button,
  Spinner,
  Text,
  ProgressBar,
  Input,
  Field,
  Checkbox,
  MessageBar,
  MessageBarBody,
  Badge,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  Sparkle20Filled,
  PlugConnected24Regular,
  CloudRegular,
  KeyRegular,
  ShieldCheckmarkRegular,
  BotSparkleFilled,
  PersonRegular,
  Info16Regular,
} from '@fluentui/react-icons';
import { ConnectionRuntimeSource, type ConnectionReference } from '@/types/connectionReference';
import type { ConnectionParameterSet, FormValues, FormValidationErrors } from '@/types/connectionParameters';
import { validateFormValues, getInitialFormValues, hasOAuthParameter, isManagedIdentityParameterSet } from '@/types/connectionParameters';
import { DynamicConnectionForm } from '../connections/forms/DynamicConnectionForm';
import { AgentConnectionForm } from '../connections/forms/AgentConnectionForm';
import type { AgentConnectionFormValues, AgentModelType } from '@/types/agentConnection';
import { useOAuthConnection } from '@/hooks/useOAuthConnection';
import { useGhcpOAuthConnection } from '@/hooks/useGhcpOAuthConnection';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useEffectiveManifestWithFallback } from '@/hooks/useEffectiveManifest';
import {
  useCreateConnection,
  useConnectorMetadata,
  useUniqueConnectionName,
  useTestConnection,
  useAppContextBuilder,
} from '@/hooks/useConnectionMutations';
import { useConnectionStore } from '@/stores/connectionStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { toConnectionReference, extractConnectionName, deleteConnection } from '@/services/connectionApi';
import { testServiceProviderConnection } from '@/services/logicAppsApi';
import { extractMiAudienceFromParameterSets } from '@/services/dynamicConnectionService';
import {
  extractOperationGroup,
  isManagedApiConnector,
  isServiceProviderConnector,
  toShortManagedApiId,
  extractServiceProviderAppSettings,
  extractAgentConnectionAppSettings,
  CONNECTION_NAME_MAX_LENGTH,
  CONNECTION_NAME_PATTERN,
  generateSuggestedConnectionName,
  normalizeConnectionReferenceKey,
  isDuplicateConnectionName,
  DUPLICATE_CONNECTION_NAME_ERROR,
  extractArmAccountName,
  normalizeAzureAgentHostUrl,
  getAgentConnectionInfoMessage,
  isAgentConnectionFormValid,
  isFoundryModelType,
  detectAutoCreateMode,
  requireAppContext,
  FALLBACK_PARAMETER_SETS,
  type AutoCreateMode,
} from '@/utils/connections';
import { ensureFoundryPermissions, type RoleAssignmentResult } from '@/services/roleAssignmentApi';
import { isLocalMode } from '@/services/localRuntimeClient';
import {
  getConnectionParameterSets,
  hasRealParameterSets,
  connectorRequiresOAuth,
  prepareParameterValuesForApi,
  validateConnectionName,
  type ConnectorManifestWithParameters,
} from '@/services/manifestConnectionService';
import { isLocalConnector } from '@/services/connectionCreationService';
import { parseAppId } from '@/types';
import { useUpsertEnvironmentSettings } from '@/hooks/useEnvironmentSettings';

const useStyles = makeStyles({
  roleAssignmentFeedback: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    maxWidth: '360px',
    textAlign: 'left' as const,
  },
  roleAssignmentSuccess: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  roleAssignmentWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
  },
  roleAssignmentSuccessText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteGreenForeground1,
  },
  roleAssignmentWarningText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteYellowForeground1,
  },
  skeletonSectionMarginTop: {
    marginTop: tokens.spacingVerticalM,
  },
  personIcon: {
    marginRight: tokens.spacingHorizontalXS,
    verticalAlign: 'middle' as const,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: tokens.spacingVerticalM,
  },

  // Compact header with progress dots
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  backButton: {
    flexShrink: 0,
  },
  headerTitleSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerIcon: {
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIconFoundry: {
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteLilacBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIconSymbol: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
  },
  headerIconSymbolFoundry: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },

  // Progress indicator - compact dots
  progressSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalXXS,
  },
  progressLabelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progressLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  progressDots: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  progressDot: {
    width: '6px',
    height: '6px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralStroke2,
  },
  progressDotActive: {
    backgroundColor: tokens.colorBrandBackground,
  },
  progressDotComplete: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  progressBar: {
    height: '3px',
    borderRadius: tokens.borderRadiusCircular,
  },

  // Main form area - compact
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
  },

  // Section styling - compact, no icons
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  sectionIcon: {
    display: 'none', // Hidden in compact mode
  },
  sectionIconSymbol: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  sectionInfoButton: {
    minWidth: 'auto',
    width: '20px',
    height: '20px',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    color: tokens.colorNeutralForeground3,

    ':hover': {
      color: tokens.colorNeutralForeground2,
    },
  },
  sectionInfoIcon: {
    fontSize: '14px',
    lineHeight: 1,
  },
  sectionDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: tokens.spacingVerticalM,
  },

  // Provider type selector (GitHub Copilot OAuth / API key auth)
  providerSelector: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginBottom: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },

  // Field group - flat, no background wrapper
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  field: {
    '& label': {
      fontWeight: tokens.fontWeightMedium,
      color: tokens.colorNeutralForeground1,
      marginBottom: tokens.spacingVerticalXXS,
    },
  },

  // Footer with actions - compact
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  securityNote: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  securityIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },

  // Status states - compact
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
    gap: tokens.spacingVerticalS,
    textAlign: 'center',
  },
  loadingText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  loadingSubtext: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },

  // Success state - compact
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
    gap: tokens.spacingVerticalS,
    textAlign: 'center',
  },
  successIcon: {
    fontSize: tokens.fontSizeHero800,
    color: tokens.colorPaletteGreenForeground1,
  },
  successTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  successSubtitle: {
    display: 'none', // Hidden in compact mode
  },

  // Error state - compact
  errorContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder1),
  },
  errorIcon: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorPaletteRedForeground1,
    flexShrink: 0,
    marginTop: '2px',
  },
  errorContent: {
    flex: 1,
  },
  errorTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorPaletteRedForeground1,
  },
  errorMessage: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorPaletteRedForeground2,
    marginTop: tokens.spacingVerticalXXS,
  },

  // Skeleton loading states - refined pulse animation
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    padding: tokens.spacingVerticalS,
  },
  skeletonSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  skeletonHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  skeletonIcon: {
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralStroke2,
    animationName: {
      '0%': { opacity: 0.4 },
      '50%': { opacity: 0.7 },
      '100%': { opacity: 0.4 },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  skeletonTitle: {
    width: '140px',
    height: '16px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralStroke2,
    animationName: {
      '0%': { opacity: 0.4 },
      '50%': { opacity: 0.7 },
      '100%': { opacity: 0.4 },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    animationDelay: '0.1s',
  },
  skeletonDescription: {
    width: '220px',
    height: '12px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralStroke2,
    animationName: {
      '0%': { opacity: 0.3 },
      '50%': { opacity: 0.6 },
      '100%': { opacity: 0.3 },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    animationDelay: '0.2s',
  },
  skeletonField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  skeletonLabel: {
    width: '80px',
    height: '14px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralStroke2,
    animationName: {
      '0%': { opacity: 0.4 },
      '50%': { opacity: 0.7 },
      '100%': { opacity: 0.4 },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    animationDelay: '0.3s',
  },
  skeletonInput: {
    width: '100%',
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralStroke2,
    animationName: {
      '0%': { opacity: 0.3 },
      '50%': { opacity: 0.5 },
      '100%': { opacity: 0.3 },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    animationDelay: '0.4s',
  },
  skeletonButton: {
    width: '100%',
    height: '36px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    animationName: {
      '0%': { opacity: 0.5 },
      '50%': { opacity: 0.8 },
      '100%': { opacity: 0.5 },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    animationDelay: '0.5s',
  },

  // Connector icon - compact
  connectorIcon: {
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusMedium,
    objectFit: 'contain',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '4px',
  },

  // Connection name section - prominent styling for P0.1
  connectionNameSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalM,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    marginBottom: tokens.spacingVerticalS,
  },
  // P2.1: Header row with title and badge
  connectionNameHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  connectionNameTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  connectionNameHelper: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  connectionNameInputWrapper: {
    position: 'relative' as const,
  },
  connectionNameInput: {
    width: '100%',
  },
  connectionNameSuggested: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    marginTop: tokens.spacingVerticalXXS,
  },
  connectionNameCharCount: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
  },
  connectionNameFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalXXS,
  },

  // Auto-create UI - P0.3
  autoCreateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalM,
    textAlign: 'center',
  },
  autoCreateSpinner: {
    marginBottom: tokens.spacingVerticalS,
  },
  autoCreateTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  autoCreateSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  autoCreateCancel: {
    marginTop: tokens.spacingVerticalM,
  },

  // Dynamic/OBO connection - P0.4
  dynamicConnectionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    marginTop: tokens.spacingVerticalS,
  },
  dynamicConnectionCheckbox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
  },
  dynamicConnectionHelper: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: '28px', // Align with checkbox label
  },
  dynamicConnectionWarning: {
    marginTop: tokens.spacingVerticalXS,
  },
});

type TimeoutRef = {
  current: ReturnType<typeof setTimeout> | null;
};

function clearTimeoutRef(timeoutRef: TimeoutRef): void {
  if (timeoutRef.current === null) {
    return;
  }

  clearTimeout(timeoutRef.current);
  timeoutRef.current = null;
}

/**
 * Pre-flight a service-provider connection against the runtime so auth / cert /
 * RBAC failures surface inline before we persist to connections.json. Mirrors
 * Logic Apps Designer's `_testServiceProviderConnection`. Rethrows with a
 * `Connection test failed: ...` prefix so the form picks up the message.
 */
async function preflightServiceProviderConnection(opts: {
  project: string;
  app: string;
  getToken: () => Promise<string | null>;
  connectorId: string;
  connectionName: string;
  parameterValues: Record<string, unknown>;
  parameterSetName: string | undefined;
}): Promise<void> {
  try {
    await testServiceProviderConnection({
      projectName: opts.project,
      appName: opts.app,
      getToken: opts.getToken,
      operationGroup: extractOperationGroup(opts.connectorId),
      body: {
        parameterValues: opts.parameterValues,
        ...(opts.parameterSetName ? { parameterSetName: opts.parameterSetName } : {}),
        serviceProvider: { id: opts.connectorId },
        displayName: opts.connectionName,
      },
    });
  } catch (err) {
    const message = err instanceof Error
      ? err.message
      : 'Verify your credentials and try again.';
    throw new Error(`Connection test failed: ${message}`);
  }
}

export interface ConnectionCreationPanelProps {
  /** Title for the panel */
  title?: string;
  /** Connector ID for API connections */
  connectorId?: string;
  /** Connector type (e.g., 'AzureOpenAI', 'office365') */
  connectorType?: string;
  /** Connector display name */
  connectorName?: string;
  /** Connector icon URL */
  connectorIcon?: string;
  /** Connector manifest (for manifest-driven forms) */
  connectorManifest?: ConnectorManifestWithParameters | null;
  /** Agent model type for Agent connectors (e.g., 'AzureOpenAI') */
  agentModelType?: AgentModelType;
  /**
   * Agent variant — when `'coding'`, a provider type selector is shown so
   * the user can switch between GitHub Copilot (OAuth) and API key auth.
   * `'hosted'` is accepted for type-compatibility with callers passing the
   * full AgentVariant union, but hosted agents never render this panel (they
   * skip the model-connection step), so it has no behavioural effect here.
   */
  agentVariant?: 'native' | 'foundry' | 'coding' | 'hosted';
  /**
   * Extra properties to unconditionally merge into connectionProperties after
   * the connection resource is created. Used by ConnectionPicker to inject
   * context-derived values (e.g. azureAgentAIResourceName) that the OAuth
   * form cannot capture.
   */
  connectionPropertyOverrides?: Record<string, unknown>;
  /** Callback when creation is complete */
  onComplete: (referenceKey: string, reference: ConnectionReference) => void;
  /** Callback to go back */
  onBack: () => void;
}

/**
 * ConnectionCreationPanel - Create new connection inline
 */
export const ConnectionCreationPanel = ({
  title,
  connectorId,
  connectorType,
  connectorName,
  connectorIcon,
  connectorManifest,
  agentModelType,
  agentVariant,
  connectionPropertyOverrides,
  onComplete,
  onBack,
}: ConnectionCreationPanelProps) => {
  const styles = useStyles();
  const [step, setStep] = useState<'form' | 'creating' | 'success' | 'error'>('form');

  // Generated once per mount so the input default and the placeholder/hint
  // stay in sync, and so the value is set synchronously before any child effect
  // can call onChange with a stale empty displayName.
  const [suggestedConnectionName] = useState(() =>
    generateSuggestedConnectionName(connectorName, connectorType),
  );

  const [formValues, setFormValues] = useState<FormValues>(() => ({
    displayName: suggestedConnectionName,
  }));
  const [validationErrors, setValidationErrors] = useState<FormValidationErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [selectedParameterSet, setSelectedParameterSet] = useState<string | undefined>();
  const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null);
  const [connectionNameFormatError, setConnectionNameFormatError] = useState<string | null>(null);

  // Auto-create state - P0.3
  const [autoCreateTriggered, setAutoCreateTriggered] = useState(false);
  const [autoCreateMode, setAutoCreateMode] = useState<AutoCreateMode>('manual');
  // Ref guard to prevent duplicate auto-create triggers across rapid re-renders
  const autoCreateInitiatedRef = useRef(false);
  // Permanent bypass: once the user cancels auto-create, don't re-trigger it
  const userCancelledAutoCreateRef = useRef(false);
  const autoCreateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dynamic/OBO connection state - P0.4
  const [createAsDynamic, setCreateAsDynamic] = useState(false);

  const [agentFormValues, setAgentFormValues] = useState<AgentConnectionFormValues>(() => ({
    displayName: suggestedConnectionName,
  }));
  const [agentFormErrors, setAgentFormErrors] = useState<Record<string, string>>({});
  const [roleAssignmentResult, setRoleAssignmentResult] = useState<RoleAssignmentResult | null>(null);

  // App context for role assignment
  const selectedAcaId = useAppStore((state) => state.selectedApp?.armResourceId);
  const selectedProject = useAppStore((state) => state.selectedProject);
  const selectedAppName = useAppStore((state) => state.selectedAppName);
  const { getArmToken, getOttoToken } = useAuth();

  // Mutation for writing service-provider connection secrets into app settings.
  // Safe to call with an empty `projectName`/`appName` — the mutation simply
  // becomes a no-op (the SP create flow short-circuits when there are no
  // secrets, and we guard for app context before invoking).
  const upsertAppSettingsMutation = useUpsertEnvironmentSettings(
    selectedProject ?? '',
    selectedAppName ?? '',
  );

  // Check if this is an Agent connector that should use the specialized form
  const isAgentConnector = connectorType === 'Agent' || connectorType === 'AgentLoop';

  // Provider selector for coding agents — allows the user to switch between
  // API Key (V1ChatCompletionsService) and GitHubCopilot (OAuth) without
  // leaving the creation flow. Only shown when the caller passes
  // `agentVariant='coding'`. Defaults to GitHubCopilot since that's the
  // primary model provider for coding agents.
  const [selectedProviderOverride, setSelectedProviderOverride] = useState<AgentModelType | null>(null);

  // The provider selector (GitHub Copilot OAuth / API Key) is the GitHub Copilot
  // coding-agent feature. It is gated ENTIRELY behind a feature flag: the runtime
  // must understand the `GitHubCopilot` agent-connection type before the portal is
  // allowed to emit it — otherwise the whole connections.json fails to deserialize
  // on an older runtime, breaking every connection in the app. When the flag is
  // off the coding flow behaves exactly as it did before this feature (uses the
  // caller-supplied `agentModelType`, no selector), so flag-off is a true no-op.
  const isGhcpConnectionEnabled = useFeatureFlag('githubCopilotConnection');

  const codingProviderOptions: { type: AgentModelType; label: string }[] = [
    { type: 'GitHubCopilot', label: 'Sign in with GitHub' },
    { type: 'V1ChatCompletionsService', label: 'API Key' },
  ];
  const showProviderSelector =
    isGhcpConnectionEnabled && agentVariant === 'coding' && isAgentConnector && !!agentModelType;
  const effectiveAgentModelType = showProviderSelector
    ? (selectedProviderOverride ?? 'GitHubCopilot')
    : agentModelType;
  const useAgentForm = isAgentConnector && effectiveAgentModelType;

  // GitHub Copilot OAuth flow (reuses repo OAuth infrastructure with ottogithub connector)
  const ghcpOAuth = useGhcpOAuthConnection();
  const ghcpReset = ghcpOAuth.reset;
  const ghcpResult = ghcpOAuth.result;

  // Ensure any in-flight popup/polling is cleaned up if the panel unmounts.
  useEffect(() => {
    return () => {
      ghcpReset();
    };
  }, [ghcpReset]);

  // Get existing connection names for duplicate detection
  // Use shallow comparison to prevent unnecessary re-renders
  const references = useConnectionStore((state) => state.references);
  const existingConnectionNames = useMemo(() =>
    Object.values(references)
      .map((ref) => ref.connectionName?.toLowerCase())
      .filter((name): name is string => !!name),
    [references]
  );

  // OAuth connection hook
  const {
    state: oauthState,
    error: oauthError,
    connection: oauthConnection,
    testResult: oauthTestResult,
    createOAuthConnection,
    cancel: cancelOAuth,
    isInProgress: isOAuthInProgress,
  } = useOAuthConnection();

  // API connection creation hook
  const createConnectionMutation = useCreateConnection();

  // Post-create validation hooks (mirrors OAuth pattern in useOAuthConnection).
  const testConnectionMutation = useTestConnection();
  const { buildContext: buildAppContext } = useAppContextBuilder();

  // Unique connection naming hook
  const { getUniqueName } = useUniqueConnectionName();

  // Fetch connector metadata from Azure (includes connection parameters for OAuth detection)
  // Only fetches for managed API connectors (those with /managedApis/ in the ID)
  const { data: fetchedConnectorMetadata, isLoading: isLoadingMetadata } = useConnectorMetadata(connectorId);

  // Determine if we should show loading skeleton
  // Show skeleton while fetching metadata for managed API connectors (those needing Azure fetch)
  const isManagedApi = isManagedApiConnector(connectorId ?? null);
  const showMetadataLoading = isManagedApi && isLoadingMetadata && !connectorManifest;

  // Build effective manifest from prop or fetched metadata using shared hook
  const effectiveManifest = useEffectiveManifestWithFallback(connectorManifest, fetchedConnectorMetadata);

  // Get parameter sets from manifest or fallback
  const parameterSets = useMemo((): ConnectionParameterSet[] => {
    // First try to get from effective manifest (prop or fetched)
    if (effectiveManifest) {
      const sets = getConnectionParameterSets(effectiveManifest);
      if (sets.length > 0) {
        return sets;
      }
    }

    // Fall back to predefined sets for known connectors
    const connectorKey = connectorType ?? connectorName ?? '';
    const fallback = FALLBACK_PARAMETER_SETS[connectorKey];
    if (fallback) {
      return fallback;
    }

    // Default minimal parameter set
    return [
      {
        name: 'default',
        uiDefinition: {
          displayName: 'Connection Details',
          description: 'Enter connection details',
        },
        parameters: {
          displayName: {
            type: 'string',
            uiDefinition: {
              displayName: 'Connection Name',
              description: 'A friendly name for this connection',
              constraints: { required: 'true' },
            },
          },
        },
      },
    ];
  }, [effectiveManifest, connectorType, connectorName]);

  // Whether the connector has real multi-auth parameter sets from the API Hub
  // (as opposed to a single synthetic set from convertLegacyParameters).
  // Only real sets should send parameterSetName in create requests.
  const isRealMultiAuth = useMemo(
    () => hasRealParameterSets(effectiveManifest),
    [effectiveManifest]
  );

  // Initialize selectedParameterSet from the first available set for real multi-auth connectors.
  // This ensures parameterSetName is always sent, even if the user doesn't touch the dropdown.
  useEffect(() => {
    if (!selectedParameterSet && isRealMultiAuth && parameterSets.length > 1) {
      setSelectedParameterSet(parameterSets[0]?.name);
    }
  }, [parameterSets, selectedParameterSet, isRealMultiAuth]);

  // Get current parameter set
  const currentParameterSet = useMemo(() => {
    if (!selectedParameterSet && parameterSets.length > 0) {
      return parameterSets[0];
    }
    return parameterSets.find((s) => s.name === selectedParameterSet) ?? parameterSets[0];
  }, [parameterSets, selectedParameterSet]);

  // Track the current parameter set name to detect changes
  const [initializedParameterSet, setInitializedParameterSet] = useState<string | null>(null);

  // Initialize form values when parameter set changes
  useEffect(() => {
    const parameterSetName = currentParameterSet?.name;
    if (parameterSetName && parameterSetName !== initializedParameterSet) {
      const initialValues = getInitialFormValues(currentParameterSet);
      setFormValues((prevValues) => {
        // Preserve connection name if already entered
        if (prevValues.displayName) {
          initialValues.displayName = prevValues.displayName;
        }
        return initialValues;
      });
      setValidationErrors({});
      setInitializedParameterSet(parameterSetName);
    }
  }, [currentParameterSet, initializedParameterSet]);

  // Check if current auth type requires OAuth
  const requiresOAuth = useMemo(() => {
    if (currentParameterSet) {
      return hasOAuthParameter(currentParameterSet);
    }
    return connectorRequiresOAuth(connectorManifest, selectedParameterSet);
  }, [currentParameterSet, connectorManifest, selectedParameterSet]);

  // Dynamic/OBO connections are for conversational workflows (kind: Agent) only,
  // which are not supported in Otto preview. Hidden for now.
  const showDynamicConnectionOption = false;

  useEffect(() => {
    return () => {
      clearTimeoutRef(autoCreateTimeoutRef);
      clearTimeoutRef(completionTimeoutRef);
    };
  }, []);

  // Detect auto-create mode when parameter set is ready - P0.3
  useEffect(() => {
    // Only detect once and only for non-Agent connectors (Agent connectors need resource selection)
    if (userCancelledAutoCreateRef.current || autoCreateInitiatedRef.current || autoCreateTriggered || useAgentForm || !currentParameterSet) return;

    // Don't auto-create while metadata is still loading
    if (showMetadataLoading) return;

    const mode = detectAutoCreateMode(currentParameterSet);
    setAutoCreateMode(mode);

    // Auto-trigger OAuth flow for OAuth-only connectors
    if (mode === 'oauth') {
      if (!connectorId) {
        // Can't auto-create without a connector ID — fall through to manual form
        return;
      }

      // Don't auto-sign-in for multi-auth connectors - let users see the auth type selector
      // and click "Sign in" manually after reviewing the available authentication options
      if (isRealMultiAuth) {
        return;
      }

      // azureagentservice requires azureAgentAIResourceName in connectionProperties at runtime.
      // That value isn't available from the OAuth flow itself, so we require the user to
      // enter it in the form first. If it isn't already known via connectionPropertyOverrides,
      // fall through to the manual form so the user can fill in the resource name field.
      const isAzureAgentService = connectorId.toLowerCase().includes('azureagentservice');
      if (isAzureAgentService && !connectionPropertyOverrides?.azureAgentAIResourceName) {
        return;
      }

      // Use ref for immediate synchronous guard (state updates are batched)
      autoCreateInitiatedRef.current = true;
      
      // Generate a connection name automatically
      const autoName = suggestedConnectionName;
      setFormValues((prev) => ({ ...prev, displayName: autoName }));
      setAutoCreateTriggered(true);

      clearTimeoutRef(autoCreateTimeoutRef);

      // Trigger OAuth flow after a brief delay to show UI feedback
      autoCreateTimeoutRef.current = setTimeout(() => {
        autoCreateTimeoutRef.current = null;
        const autoCreateParams: Parameters<typeof createOAuthConnection>[0] = {
          connectorId,
          connectionName: autoName,
        };
        // Include parameterSetName if this is a multi-auth connector
        if (isRealMultiAuth && currentParameterSet?.name) {
          autoCreateParams.parameterSetName = currentParameterSet.name;
        }
        createOAuthConnection(autoCreateParams).catch((err: unknown) => {
          // Surface errors from the auto-create OAuth flow
          console.error('[ConnectionCreationPanel] Auto-create OAuth failed:', err);
          setError(err instanceof Error ? err.message : 'Auto-create failed');
          setStep('error');
        });
      }, 500);
    }
  }, [
    currentParameterSet,
    autoCreateTriggered,
    useAgentForm,
    showMetadataLoading,
    suggestedConnectionName,
    connectorId,
    connectorName,
    connectorType,
    createOAuthConnection,
    parameterSets.length,
    isRealMultiAuth,
    connectionPropertyOverrides,
  ]);

  // Sync auto-create OAuth flow completion with panel step
  // The auto-create flow fires createOAuthConnection without awaiting the result,
  // so we need to watch oauthState and update step/onComplete when it finishes.
  useEffect(() => {
    if (!autoCreateTriggered || autoCreateMode !== 'oauth') return;
    // Guard against re-firing after step has already transitioned
    if (step !== 'form') return;

    // Test failure with existing connection = soft success (matches handleOAuthClick behavior).
    // oauthTestResult is only set after testConnection runs, meaning consent was already confirmed.
    const isTestFailureWithConnection = oauthState === 'error' && oauthConnection && oauthTestResult !== null;
    const isSuccess = oauthState === 'success' || isTestFailureWithConnection;

    if (isSuccess && oauthConnection) {
      if (!connectorId) {
        setStep('error');
        setError('Missing connector ID after OAuth flow');
        return;
      }
      // Use the BE-returned ARM resource name as the reference key so it
      // matches what the connection list API echoes back on reload. Lowercasing
      // here caused the picker to render both a lower-cased local entry and a
      // case-preserved API entry, leaving stale `mapping[nodeId]` lookups
      // unresolvable after re-selection.
      const referenceKey = extractConnectionName(oauthConnection.name);
      const normalizedApiId = isManagedApiConnector(connectorId) ? toShortManagedApiId(connectorId) : connectorId;
      const reference: ConnectionReference = {
        api: { id: normalizedApiId },
        connection: { id: oauthConnection.id },
        connectionName: extractConnectionName(oauthConnection.name),
      };
      
      if (oauthConnection.properties.connectionRuntimeUrl) {
        reference.connectionRuntimeUrl = oauthConnection.properties.connectionRuntimeUrl;
      }

      // Apply connectionPropertyOverrides (e.g. azureAgentAIResourceName for azureagentservice).
      // The auto-create path bypasses handleCreateConnection so we must apply overrides here.
      const mergedAutoProps: Record<string, unknown> = {
        ...(connectionPropertyOverrides ?? {}),
      };
      const normalizedFormAzureAgentHostUrl = normalizeAzureAgentHostUrl(formValues.azureAgentAIResourceName);
      if (normalizedFormAzureAgentHostUrl) {
        mergedAutoProps.azureAgentAIResourceName = normalizedFormAzureAgentHostUrl;
      }
      const normalizedOverrideAzureAgentHostUrl = normalizeAzureAgentHostUrl(mergedAutoProps.azureAgentAIResourceName);
      if (normalizedOverrideAzureAgentHostUrl) {
        mergedAutoProps.azureAgentAIResourceName = normalizedOverrideAzureAgentHostUrl;
      }
      if (Object.keys(mergedAutoProps).length > 0) {
        reference.connectionProperties = {
          ...reference.connectionProperties,
          ...mergedAutoProps,
        };
      }

      setStep('success');
      clearTimeoutRef(completionTimeoutRef);

      completionTimeoutRef.current = setTimeout(() => {
        completionTimeoutRef.current = null;
        onComplete(referenceKey, reference);
      }, 500);
      return;
    }
    
    if (oauthState === 'error') {
      clearTimeoutRef(completionTimeoutRef);
      setError(oauthError ?? 'OAuth authentication failed');
      setStep('error');
    }
  }, [oauthState, autoCreateTriggered, autoCreateMode, oauthConnection, oauthTestResult, connectorId, oauthError, onComplete, step, formValues, connectionPropertyOverrides]);

  // Handle parameter set change
  const handleParameterSetChange = useCallback((setName: string) => {
    setSelectedParameterSet(setName);
  }, []);

  // Handle form value changes (preserves displayName from prominent field when parameter set changes)
  const handleFormChange = useCallback((values: FormValues) => {
    setFormValues((prevValues) => {
      // Preserve the displayName from the prominent field if it was already set
      // This prevents losing the connection name when switching auth methods
      const preservedDisplayName = prevValues.displayName;
      if (preservedDisplayName && !values.displayName) {
        return { ...values, displayName: preservedDisplayName };
      }
      return values;
    });
    // Clear validation errors on change
    setValidationErrors({});

    // Check for duplicate connection name (use new or preserved value)
    setDuplicateNameError(
      isDuplicateConnectionName(values.displayName as string | undefined, existingConnectionNames)
        ? DUPLICATE_CONNECTION_NAME_ERROR
        : null,
    );
  }, [existingConnectionNames]);

  // Handle Agent form value changes
  const handleAgentFormChange = useCallback((values: AgentConnectionFormValues) => {
    setAgentFormValues(values);
    // Clear validation errors on change
    setAgentFormErrors({});

    // Check for duplicate connection name
    setDuplicateNameError(
      isDuplicateConnectionName(values.displayName, existingConnectionNames)
        ? DUPLICATE_CONNECTION_NAME_ERROR
        : null,
    );
  }, [existingConnectionNames]);

  // Unified handler for connection name changes (used by prominent name field)
  const handleConnectionNameChange = useCallback((newName: string) => {
    // Enforce max length
    const truncatedName = newName.slice(0, CONNECTION_NAME_MAX_LENGTH);

    if (useAgentForm) {
      setAgentFormValues((prev) => ({
        ...prev,
        displayName: truncatedName,
      }));
      setAgentFormErrors((prev) => {
        const next = { ...prev };
        delete next.displayName;
        return next;
      });
    } else {
      setFormValues((prev) => ({
        ...prev,
        displayName: truncatedName,
      }));
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.displayName;
        return next;
      });
    }

    // GHCP OAuth: if the user changes the name after authenticating,
    // invalidate the OAuth result since it was bound to the old name.
    if (ghcpResult) {
      ghcpReset();
    }

    // Real-time format validation
    if (truncatedName) {
      if (truncatedName.length < 2) {
        setConnectionNameFormatError('Name must be at least 2 characters');
      } else if (!CONNECTION_NAME_PATTERN.test(truncatedName)) {
        setConnectionNameFormatError('Must start with a letter and contain only letters, numbers, underscores, and hyphens');
      } else {
        setConnectionNameFormatError(null);
      }
    } else {
      setConnectionNameFormatError(null);
    }

    // Check for duplicate connection name
    setDuplicateNameError(
      isDuplicateConnectionName(truncatedName, existingConnectionNames)
        ? DUPLICATE_CONNECTION_NAME_ERROR
        : null,
    );
  }, [useAgentForm, existingConnectionNames, ghcpResult, ghcpReset]);

  // Get current connection name from the appropriate form state
  const currentConnectionName = useMemo(() => {
    if (useAgentForm) {
      return agentFormValues.displayName ?? '';
    }
    return (formValues.displayName as string) ?? '';
  }, [useAgentForm, agentFormValues.displayName, formValues.displayName]);

  // Get connection name validation error (combines format error, duplicate error, and form-level errors)
  const connectionNameError = useMemo(() => {
    // Format error takes precedence (real-time validation)
    if (connectionNameFormatError) {
      return connectionNameFormatError;
    }
    // Duplicate name error
    if (duplicateNameError) {
      return duplicateNameError;
    }
    if (useAgentForm) {
      return agentFormErrors.displayName;
    }
    const error = validationErrors.displayName;
    if (error) {
      return typeof error === 'string' ? error : error.message;
    }
    return undefined;
  }, [connectionNameFormatError, duplicateNameError, useAgentForm, agentFormErrors.displayName, validationErrors.displayName]);

  // Validate form
  const isValid = useMemo(() => {
    // Block submission if connection name has any errors (format, duplicate, etc.)
    if (connectionNameError) return false;

    // For Agent form, gate the button with the shared validity helper. It
    // branches Foundry types through isFoundryModelType(), which is what fixes
    // the FoundryAgentServiceV2 button staying disabled. The submit path
    // (handleCreateAgentConnection) keeps its own per-field error messaging but
    // also routes Foundry through isFoundryModelType(), so both agree on V2.
    if (useAgentForm && effectiveAgentModelType) {
      // GHCP isn't covered by the shared helper: it only needs a display name
      // (OAuth handles the rest) and must have a successful auth/test result.
      if (effectiveAgentModelType === 'GitHubCopilot') {
        return !!agentFormValues.displayName && ghcpResult?.testResult?.success === true;
      }
      return isAgentConnectionFormValid(effectiveAgentModelType, agentFormValues);
    }

    if (!currentParameterSet) return false;
    const errors = validateFormValues(formValues, currentParameterSet);
    return Object.keys(errors).length === 0;
  }, [formValues, currentParameterSet, useAgentForm, agentFormValues, effectiveAgentModelType, connectionNameError, ghcpResult]);

  // Handle OAuth button click
  const handleOAuthClick = useCallback(async () => {
    if (!currentParameterSet || !connectorId) {
      setError('Missing connector information for OAuth');
      return;
    }

    try {
      // Get a unique connection name (checks locally and in Azure API Hub)
      const baseConnectorName = connectorName ?? connectorType ?? 'connection';
      const connectionName = (formValues.displayName as string) ||
        await getUniqueName(connectorId, baseConnectorName, existingConnectionNames);

      // Build params object - only include parameterSetName if defined
      const nonSecretConnectionParameters: Record<string, unknown> = {};
      const oauthParameterValues = prepareParameterValuesForApi(formValues, currentParameterSet);

      if (currentParameterSet && Object.keys(oauthParameterValues).length > 0) {
        for (const [key, param] of Object.entries(currentParameterSet.parameters)) {
          if (param.type === 'securestring') continue;
          if (oauthParameterValues[key] !== undefined) {
            nonSecretConnectionParameters[key] = oauthParameterValues[key];
          }
        }
      }

      if (
        formValues.azureAgentAIResourceName !== undefined &&
        formValues.azureAgentAIResourceName !== ''
      ) {
        const normalizedFormAzureAgentHostUrl = normalizeAzureAgentHostUrl(formValues.azureAgentAIResourceName);
        if (normalizedFormAzureAgentHostUrl) {
          nonSecretConnectionParameters.azureAgentAIResourceName = normalizedFormAzureAgentHostUrl;
        }
      }

      if (connectionPropertyOverrides && Object.keys(connectionPropertyOverrides).length > 0) {
        Object.assign(nonSecretConnectionParameters, connectionPropertyOverrides);
      }

      const oauthParams: Parameters<typeof createOAuthConnection>[0] = {
        connectionName,
        connectorId,
        parameterValues: oauthParameterValues,
        ...(Object.keys(nonSecretConnectionParameters).length > 0
          ? { connectionParameters: nonSecretConnectionParameters }
          : {}),
      };
      // Always include parameterSetName for multi-auth connectors
      const effectiveSetName = selectedParameterSet ?? (isRealMultiAuth ? currentParameterSet?.name : undefined);
      if (effectiveSetName) {
        oauthParams.parameterSetName = effectiveSetName;
      }
      const result = await createOAuthConnection(oauthParams);

      if (!result.connection) {
        // OAuth was cancelled or failed - use error from result (synchronous)
        setError(result.error ?? 'Authorization was cancelled or failed. Please try again.');
        setStep('error');
        return;
      }

      // Key the reference by the BE-returned resource name (case-preserved)
      // so subsequent reloads through the connection list API resolve to the
      // same entry. See referenceKey note in the auto-OAuth path above.
      const referenceKey = extractConnectionName(result.connection.name);
      const normalizedApiId = isManagedApiConnector(connectorId) ? toShortManagedApiId(connectorId) : connectorId;
      const reference: ConnectionReference = {
        api: { id: normalizedApiId },
        connection: { id: result.connection.id },
        connectionName: extractConnectionName(result.connection.name),
      };
      if (result.connection.properties.connectionRuntimeUrl) {
        reference.connectionRuntimeUrl = result.connection.properties.connectionRuntimeUrl;
      }

      // Apply connectionPropertyOverrides (e.g. azureAgentAIResourceName for azureagentservice).
      // The manual OAuth click path also bypasses handleCreateConnection.
      const mergedOAuthProps: Record<string, unknown> = {
        ...(connectionPropertyOverrides ?? {}),
      };
      const normalizedFormAzureAgentHostUrl = normalizeAzureAgentHostUrl(formValues.azureAgentAIResourceName);
      if (normalizedFormAzureAgentHostUrl) {
        mergedOAuthProps.azureAgentAIResourceName = normalizedFormAzureAgentHostUrl;
      }
      const normalizedOverrideAzureAgentHostUrl = normalizeAzureAgentHostUrl(mergedOAuthProps.azureAgentAIResourceName);
      if (normalizedOverrideAzureAgentHostUrl) {
        mergedOAuthProps.azureAgentAIResourceName = normalizedOverrideAzureAgentHostUrl;
      }
      if (Object.keys(mergedOAuthProps).length > 0) {
        reference.connectionProperties = {
          ...reference.connectionProperties,
          ...mergedOAuthProps,
        };
      }

      // P0.4: Set dynamic/OBO connection properties
      if (createAsDynamic) {
        reference.runtimeSource = ConnectionRuntimeSource.Dynamic;
        // Note: dynamicConnectionProxyUrl is set by the backend when the connection
        // is used at runtime. The runtimeSource flag tells the runtime to resolve
        // user credentials dynamically.
      }

      setStep('success');
      setTimeout(() => {
        onComplete(referenceKey, reference);
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OAuth authentication failed');
      setStep('error');
    }
  }, [
    currentParameterSet,
    connectorId,
    connectorName,
    connectorType,
    formValues,
    selectedParameterSet,
    createOAuthConnection,
    onComplete,
    existingConnectionNames,
    getUniqueName,
    createAsDynamic,
    isRealMultiAuth,
    connectionPropertyOverrides,
  ]);

  // Handle create for Agent connections
  // Agent connections are LOCAL - they are NOT created via the Azure Connections API.
  // They are stored directly in connections.json when the workflow is saved.
  const handleCreateAgentConnection = useCallback(async () => {
    // Validate required fields
    const errors: Record<string, string> = {};

    if (!agentFormValues.displayName) {
      errors.displayName = 'Connection name is required';
    }

    // Validate based on agentModelType
    if (effectiveAgentModelType === 'AzureOpenAI') {
      if (!agentFormValues.subscriptionId) errors.subscriptionId = 'Subscription is required';
      if (!agentFormValues.accountId) errors.accountId = 'Azure OpenAI resource is required';
    } else if (isFoundryModelType(effectiveAgentModelType)) {
      if (!agentFormValues.subscriptionId) errors.subscriptionId = 'Subscription is required';
      if (!agentFormValues.projectId) errors.projectId = 'AI Foundry project is required';
      if (!agentFormValues.endpoint) errors.endpoint = 'Project endpoint is required';
    } else if (effectiveAgentModelType === 'GitHubCopilot') {
      // GHCP: OAuth must have been completed successfully (no manual fields needed).
      // Use a dedicated 'oauth' key so the sign-in message doesn't overwrite
      // a more actionable "Connection name is required" error on displayName.
      if (ghcpResult?.testResult?.success !== true) {
        errors.oauth = 'Please sign in with GitHub first';
      }
    } else {
      if (!agentFormValues.endpoint) errors.endpoint = 'Endpoint URL is required';
      if (!agentFormValues.apiKey) errors.apiKey = 'API key is required';
    }

    if (Object.keys(errors).length > 0) {
      setAgentFormErrors(errors);
      return;
    }

    // Validate connection name format
    const connectionName = agentFormValues.displayName;
    const nameValidation = validateConnectionName(connectionName);
    if (!nameValidation.valid) {
      setAgentFormErrors({ displayName: nameValidation.error ?? 'Invalid connection name' });
      return;
    }

    setStep('creating');
    setError(null);

    try {
      // Build connector ID for Agent connections - use the standard pattern
      // This is a LOCAL connector ID, NOT an Azure ARM resource ID
      const effectiveConnectorId = connectorId ?? `connectionProviders/agent`;

      // Generate reference key from connection name
      const referenceKey = normalizeConnectionReferenceKey(connectionName);

      // Determine the connection type based on agentModelType. Persisted as
      // connectionProperties.type and used downstream to classify the connection
      // (e.g. by AgentTypeView's native vs. Foundry picker). Must be a concrete
      // provider name — the generic 'model' marker does not classify.
      let connectionType: string = effectiveAgentModelType ?? 'model';
      if (isFoundryModelType(effectiveAgentModelType)) {
        connectionType = 'FoundryAgentService';
      } else if (effectiveAgentModelType === 'APIMGenAIGateway') {
        connectionType = 'APIMGenAIGateway';
      } else if (effectiveAgentModelType === 'GitHubCopilot') {
        connectionType = 'GitHubCopilot';
      }

      // Build connection properties that will be stored in connections.json
      const connectionProperties: Record<string, unknown> = {
        type: connectionType,
      };

      // GitHubCopilot: endpoint is implicit. The runtime resolves the underlying
      // APIHub connection via BPM using the agent connection reference name —
      // no connectionId needs to be stored on the agent connection itself.
      if (effectiveAgentModelType === 'GitHubCopilot' && ghcpResult?.testResult?.success === true) {
        connectionProperties.endpoint = 'https://api.githubcopilot.com';
      }

      if (effectiveAgentModelType !== 'GitHubCopilot' && agentFormValues.endpoint) {
        connectionProperties.endpoint = agentFormValues.endpoint;
      }
      if (agentFormValues.accountId) {
        connectionProperties.resourceId = agentFormValues.accountId;
        // API Hub gateway policy requires the account name (last /accounts/{name} segment)
        const accountName = extractArmAccountName(agentFormValues.accountId);
        if (accountName) {
          connectionProperties.azureAgentAIResourceName = accountName;
        }
      }
      if (agentFormValues.projectId) {
        connectionProperties.projectId = agentFormValues.projectId;
        // For Foundry agents, resourceId must be the FULL project ARM ID
        // (including /projects/{proj}). The backend FoundryProjectResourceReference
        // validates this format: .../accounts/{name}/projects/{proj}
        if (!connectionProperties.resourceId) {
          connectionProperties.resourceId = agentFormValues.projectId;
        }
        // API Hub gateway policy also requires azureAgentAIResourceName for Foundry agents
        if (!connectionProperties.azureAgentAIResourceName) {
          const accountName = extractArmAccountName(agentFormValues.projectId);
          if (accountName) {
            connectionProperties.azureAgentAIResourceName = accountName;
          }
        }
      }

      // Build the ConnectionReference directly (no API call needed for local connectors)
      // The connection will be persisted to connections.json when the workflow is saved
      const reference: ConnectionReference = {
        api: { id: effectiveConnectorId },
        connection: { id: `/${effectiveConnectorId}/connections/${referenceKey}` },
        connectionName: connectionName,
        connectionProperties,
      };

      // Set authentication based on connection type
      if (effectiveAgentModelType === 'GitHubCopilot') {
        // GHCP auth is handled entirely by the runtime (BPM token brokerage) —
        // connection.Authentication is never inspected for GitHubCopilot type.
        // 'None' satisfies the Required.Always constraint without implying
        // the connection itself carries credentials.
        reference.authentication = {
          type: 'None',
        };
        connectionProperties.authentication = {
          type: 'None',
        };
      } else if (agentFormValues.apiKey) {
        reference.authentication = {
          type: 'Key',
        };
        // Move the raw key into an app setting and store an `@appsetting()`
        // reference in connections.json. The Standard runtime's
        // `GetResolvedAgentConnections` evaluates the reference at execution
        // time — inline secrets are not accepted by service-provider-style
        // resolution and would leak into source control.
        const { authentication: redactedAuth, appSettings } =
          extractAgentConnectionAppSettings(referenceKey, {
            type: 'Key',
            key: agentFormValues.apiKey,
          });

        if (Object.keys(appSettings).length > 0) {
          if (!selectedProject || !selectedAppName) {
            throw new Error(
              'Cannot create connection: no project / application selected. ' +
              'Open an application before creating an agent connection.'
            );
          }
          await upsertAppSettingsMutation.mutateAsync(appSettings);
        }

        connectionProperties.authentication = redactedAuth;
      } else {
        // Managed Identity authentication
        reference.authentication = {
          type: 'ManagedServiceIdentity',
        };
        connectionProperties.authentication = {
          type: 'ManagedServiceIdentity',
        };
      }

      // For FoundryAgentService with MSI auth, assign "Azure AI Developer" role
      // on the Cognitive Services account so the managed identity can access agents.
      if (
        isFoundryModelType(effectiveAgentModelType) &&
        !agentFormValues.apiKey &&
        agentFormValues.projectId &&
        selectedAcaId &&
        !isLocalMode()
      ) {
        try {
          const armToken = await getArmToken();
          if (armToken) {
            const parsedAppId = parseAppId(selectedAcaId);
            if (!parsedAppId) {
              return;
            }
            
            const result = await ensureFoundryPermissions(
              parsedAppId.subscriptionId,
              parsedAppId.resourceGroup,
              parsedAppId.appName,
              agentFormValues.projectId,
              armToken
            );
            setRoleAssignmentResult(result);
            if (!result.success) {
              console.warn('Role assignment warning:', result.error);
              // Don't block connection creation — the role can be assigned manually
            }
          }
        } catch (roleErr) {
          console.warn('Role assignment failed (non-blocking):', roleErr);
          setRoleAssignmentResult({
            success: false,
            alreadyAssigned: false,
            error: roleErr instanceof Error ? roleErr.message : 'Failed to assign permissions',
          });
        }
      }

      setStep('success');

      setTimeout(() => {
        onComplete(referenceKey, reference);
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create connection');
      setStep('error');
    }
  }, [agentFormValues.displayName, agentFormValues.subscriptionId, agentFormValues.accountId, agentFormValues.projectId, agentFormValues.endpoint, agentFormValues.apiKey, effectiveAgentModelType, connectorId, selectedAcaId, selectedProject, selectedAppName, upsertAppSettingsMutation, getArmToken, onComplete, ghcpResult]);

  // Handle create (non-OAuth, standard form)
  const handleCreate = useCallback(async () => {
    // Route to Agent handler if using Agent form
    if (useAgentForm) {
      return handleCreateAgentConnection();
    }

    if (!currentParameterSet) {
      setError('No parameter set selected');
      return;
    }

    // Validate
    const validationErrors = validateFormValues(formValues, currentParameterSet);
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors(validationErrors);
      return;
    }

    setStep('creating');
    setError(null);

    try {
      // Determine the connector ID to use
      // Only fall back to agentConnections path for actual agent connectors
      const effectiveConnectorId = connectorId
        ?? (isAgentConnector ? `/agentConnections/${connectorType ?? 'connection'}` : undefined);

      if (!effectiveConnectorId) {
        throw new Error(
          'Missing connector ID. Unable to determine which connector to create a connection for.'
        );
      }

      // Check if this is a local connector - these should NOT be created via Azure ARM API
      // Local connectors (Agent, ServiceProvider, etc.) are stored locally in connections.json
      if (isLocalConnector(effectiveConnectorId)) {
        // Service provider connections are stored in connections-draft.json serviceProviderConnections
        // We build a ConnectionReference that exportToConnectionsConfig will serialize correctly
        if (isServiceProviderConnector(effectiveConnectorId)) {
          const connectionName = (formValues.displayName as string) || suggestedConnectionName;

          // Validate connection name format
          const nameValidation = validateConnectionName(connectionName);
          if (!nameValidation.valid) {
            setValidationErrors({
              displayName: { message: nameValidation.error ?? 'Invalid connection name', type: 'custom' }
            });
            setStep('form');
            return;
          }

          // Prepare parameter values from the form. Auth-provider sub-fields
          // (Tenant/ClientId/Secret/Type/Identity, ...) are nested under
          // `authProvider` automatically when the manifest declares them with
          // `propertyPath: ["authProvider"]`. This matches LogicAppsUX —
          // the runtime's `ServiceProviderEngine.GetAuthProviderSection` reads
          // `parameterValues.authProvider` directly. (Issue #371.)
          const paramValues = prepareParameterValuesForApi(formValues, currentParameterSet);

          const resolvedParameterSetName =
            selectedParameterSet ?? (isRealMultiAuth ? currentParameterSet?.name : undefined);

          // Pre-flight against the runtime so auth / cert / RBAC failures surface
          // inline before we persist to connections.json. Skipped when the
          // connector omits `testConnectionUrl` (older SPs without a
          // TestConnectionOperationName).
          if (connectorManifest?.testConnectionUrl) {
            const { project, app } = requireAppContext(selectedProject, selectedAppName, 'test connection');
            await preflightServiceProviderConnection({
              project,
              app,
              getToken: getOttoToken,
              connectorId: effectiveConnectorId,
              connectionName,
              parameterValues: paramValues,
              parameterSetName: resolvedParameterSetName,
            });
          }

          // Build a ConnectionReference for the connection store
          const referenceKey = normalizeConnectionReferenceKey(connectionName);

          // Move `parameterSource: 'AppConfiguration'` values (e.g.
          // serviceBus connectionString) into app settings and replace the
          // inline value with an `@appsetting('NAME')` reference. The
          // Standard runtime's `ServiceProviderEngine` resolves these
          // references at execution time and rejects raw inline secrets.
          const { parameterValues: storedParamValues, appSettings } =
            extractServiceProviderAppSettings(referenceKey, paramValues, currentParameterSet);

          if (Object.keys(appSettings).length > 0) {
            requireAppContext(selectedProject, selectedAppName, 'create connection');
            await upsertAppSettingsMutation.mutateAsync(appSettings);
          }

          const reference: ConnectionReference = {
            api: { id: effectiveConnectorId },
            connection: { id: '' }, // Service providers don't have ARM connection resources
            connectionName: connectionName,
            connectionProperties: {
              ...storedParamValues,
              // Store parameterSetName so exportToConnectionsConfig can include it
              ...(resolvedParameterSetName
                ? { _parameterSetName: resolvedParameterSetName }
                : {}),
            },
          };

          setStep('success');

          clearTimeoutRef(completionTimeoutRef);
          completionTimeoutRef.current = setTimeout(() => {
            completionTimeoutRef.current = null;
            onComplete(referenceKey, reference);
          }, 500);
          return;
        }

        throw new Error(
          `Cannot create connection via Azure API: "${effectiveConnectorId}" is a local connector. ` +
          `Local connectors should use the Agent connection flow. Please ensure agentModelType is set.`
        );
      }

      // Get a unique connection name (checks locally and in Azure API Hub)
      const baseConnectorName = connectorName ?? connectorType ?? 'connection';
      const connectionName = (formValues.displayName as string) ||
        await getUniqueName(effectiveConnectorId, baseConnectorName, existingConnectionNames);

      // Validate connection name format
      const nameValidation = validateConnectionName(connectionName);
      if (!nameValidation.valid) {
        setValidationErrors({
          displayName: { message: nameValidation.error ?? 'Invalid connection name', type: 'custom' }
        });
        setStep('form');
        return;
      }

      // Prepare parameter values for the API
      const parameterValues = prepareParameterValuesForApi(formValues, currentParameterSet);
      const nonSecretConnectionParameters: Record<string, unknown> = {};

      if (currentParameterSet && Object.keys(parameterValues).length > 0) {
        for (const [key, param] of Object.entries(currentParameterSet.parameters)) {
          if (param.type === 'securestring') continue;
          if (parameterValues[key] !== undefined) {
            nonSecretConnectionParameters[key] = parameterValues[key];
          }
        }
      }

      if (
        formValues.azureAgentAIResourceName !== undefined &&
        formValues.azureAgentAIResourceName !== ''
      ) {
        const normalizedFormAzureAgentHostUrl = normalizeAzureAgentHostUrl(formValues.azureAgentAIResourceName);
        if (normalizedFormAzureAgentHostUrl) {
          nonSecretConnectionParameters.azureAgentAIResourceName = normalizedFormAzureAgentHostUrl;
        }
      }

      if (connectionPropertyOverrides && Object.keys(connectionPropertyOverrides).length > 0) {
        Object.assign(nonSecretConnectionParameters, connectionPropertyOverrides);
      }

      // Call the real API to create the connection
      const connectionResource = await createConnectionMutation.mutateAsync({
        connectionName,
        connectorId: effectiveConnectorId,
        parameterValues,
        ...(Object.keys(nonSecretConnectionParameters).length > 0
          ? { connectionParameters: nonSecretConnectionParameters }
          : {}),
        ...(selectedParameterSet ?? (isRealMultiAuth ? currentParameterSet?.name : undefined))
          ? { parameterSetName: selectedParameterSet ?? currentParameterSet?.name }
          : {},
      });

      // Convert the resource to a connection reference. Key the store entry
      // by the BE-returned ARM resource name (case-preserved) so it matches
      // what the connection list API echoes back on reload — lowercasing here
      // caused the picker to render both a lower-cased local entry and a
      // case-preserved API entry, leaving stale `mapping[nodeId]` lookups
      // unresolvable after re-selection.
      const referenceKey = extractConnectionName(connectionResource.name);
      const reference = toConnectionReference(connectionResource);

      // Override the connection name with the display name
      reference.connectionName = connectionName;

      // Form state, resolved set, or BE echo — any can be authoritative.
      const effectiveParameterSetName =
        selectedParameterSet ??
        currentParameterSet?.name ??
        connectionResource.properties.parameterValueSet?.name;
      const isManagedIdentityAuth = isManagedIdentityParameterSet(
        effectiveParameterSetName,
        parameterSets
      );

      // Add authentication type if managed identity
      if (isManagedIdentityAuth) {
        reference.authentication = {
          type: 'ManagedServiceIdentity',
        };

        // The Otto control plane's POST /connections/{name}/sync (invoked
        // automatically by createConnection) handles binding the Logic
        // App's managed identity to the connection. No client-side
        // access-policy PUT is needed.
        if (selectedAppName && isManagedApiConnector(effectiveConnectorId)) {
          // Persist MI audience under connectionProperties.authentication —
          // the api-hub rejects audience on the root authentication block.
          // Consumers read it via readPersistedMiAudience.
          const audience = extractMiAudienceFromParameterSets(
            parameterSets,
            effectiveParameterSetName,
          );
          reference.connectionProperties = {
            ...reference.connectionProperties,
            authentication: {
              type: 'ManagedServiceIdentity',
              ...(audience ? { audience } : {}),
            },
          };
        }
      }

      // Merge non-secret parameter values into connectionProperties.
      // Some connectors (e.g. azureagentservice) require config values like
      // azureAgentAIResourceName to be present in connectionProperties at
      // runtime so the API Hub gateway policy can read them. The ARM
      // connection resource does not echo parameterValues back, so we carry
      // them over here, excluding securestring fields to avoid storing
      // credentials in connections.json.
      if (Object.keys(nonSecretConnectionParameters).length > 0) {
          reference.connectionProperties = {
            ...reference.connectionProperties,
            ...nonSecretConnectionParameters,
          };
      }

      // Special case: azureagentservice (and similar) connectors use OAuth with no
      // regular string parameters, so parameterValues above may be empty. But the
      // connector form may still expose azureAgentAIResourceName as a direct formValue
      // collected outside the parameterSet loop. Carry it over explicitly so the
      // API Hub gateway policy can read it at runtime.
      if (
        !reference.connectionProperties?.azureAgentAIResourceName &&
        formValues.azureAgentAIResourceName !== undefined &&
        formValues.azureAgentAIResourceName !== ''
      ) {
        const normalizedFormAzureAgentHostUrl = normalizeAzureAgentHostUrl(formValues.azureAgentAIResourceName);
        if (normalizedFormAzureAgentHostUrl) {
        reference.connectionProperties = {
          ...reference.connectionProperties,
          azureAgentAIResourceName: normalizedFormAzureAgentHostUrl,
        };
        }
      }

      // Apply any caller-supplied property overrides (e.g. azureAgentAIResourceName
      // passed in from ConnectionPicker because the OAuth form can't capture it).
      if (connectionPropertyOverrides && Object.keys(connectionPropertyOverrides).length > 0) {
        reference.connectionProperties = {
          ...reference.connectionProperties,
          ...connectionPropertyOverrides,
        };
      }

      // Mirror LogicAppsUX: validate MI connections post-create via the
      // standard testLinks/testRequests path. When Otto BE populates testLinks
      // (parity with API Hub managed-API connections), this surfaces real
      // endpoint/RBAC failures. When it doesn't, the helper falls through to
      // overallStatus — see docs/be-issues/mi-connection-testlinks.md for the
      // BE work item that closes that gap.
      if (isManagedIdentityAuth) {
        let testResult = await testConnectionMutation.mutateAsync(connectionName);
        // One retry after a short delay — MI role assignments can take a few
        // seconds to propagate after /sync.
        if (!testResult.success) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          testResult = await testConnectionMutation.mutateAsync(connectionName);
        }
        if (!testResult.success) {
          try {
            const ctx = await buildAppContext();
            if (ctx) await deleteConnection(ctx, connectionName);
          } catch (cleanupErr) {
            console.warn('Failed to delete orphaned MI connection after test failure', cleanupErr);
          }
          throw new Error(
            testResult.message
              ? `Connection test failed: ${testResult.message}`
              : 'Connection test failed. Verify the endpoint is reachable and the managed identity has the required role assignments.'
          );
        }
      }

      setStep('success');
      completionTimeoutRef.current = setTimeout(() => {
        completionTimeoutRef.current = null;
        onComplete(referenceKey, reference);
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create connection');
      setStep('error');
    }
  }, [useAgentForm, currentParameterSet, formValues, handleCreateAgentConnection, connectorId, isAgentConnector, connectorType, connectorName, getUniqueName, existingConnectionNames, connectionPropertyOverrides, createConnectionMutation, selectedParameterSet, isRealMultiAuth, suggestedConnectionName, selectedProject, selectedAppName, upsertAppSettingsMutation, onComplete, parameterSets, testConnectionMutation, buildAppContext, connectorManifest, getOttoToken]);

  // Calculate progress percentage
  const progressValue = useMemo(() => {
    if (oauthState === 'authenticating') return 0.6;
    if (oauthState === 'confirming') return 0.75;
    if (oauthState === 'testing') return 0.9;

    switch (step) {
      case 'form':
        return 0.5;
      case 'creating':
        return 0.75;
      case 'success':
        return 1;
      case 'error':
        return 0.5;
      default:
        return 0;
    }
  }, [step, oauthState]);

  // Get step label for progress
  const stepLabel = useMemo(() => {
    if (isOAuthInProgress) {
      switch (oauthState) {
        case 'creating':
          return 'Creating connection...';
        case 'authenticating':
          return 'Waiting for authorization...';
        case 'confirming':
          return 'Confirming authorization...';
        case 'testing':
          return 'Testing connection...';
        default:
          return 'Authenticating...';
      }
    }

    switch (step) {
      case 'form':
        return 'Configure connection';
      case 'creating':
        return 'Creating connection...';
      case 'success':
        return 'Complete!';
      case 'error':
        return 'Configure connection';
      default:
        return '';
    }
  }, [step, oauthState, isOAuthInProgress]);

  // Get header icon
  const getHeaderIcon = () => {
    if (connectorIcon) {
      return (
        <img
          src={connectorIcon}
          alt=""
          className={styles.connectorIcon}
          aria-hidden="true"
        />
      );
    }

    // Foundry agents get a branded purple icon
    if (isFoundryModelType(effectiveAgentModelType)) {
      return <BotSparkleFilled className={styles.headerIconSymbolFoundry} />;
    }

    const iconType = connectorType ?? connectorName ?? '';
    switch (iconType) {
      case 'AzureOpenAI':
        return <CloudRegular className={styles.headerIconSymbol} />;
      case 'OpenAI':
        return <Sparkle20Filled className={styles.headerIconSymbol} />;
      default:
        return <PlugConnected24Regular className={styles.headerIconSymbol} />;
    }
  };

  // Render form
  const renderForm = () => (
    <div className={styles.form}>
      {/* Prominent Connection Name Section - P0.1, P2.1 */}
      <div className={styles.connectionNameSection}>
        <div className={styles.connectionNameHeader}>
          <Text className={styles.connectionNameTitle}>
            {isAgentConnector ? 'Name your AI connection' : 'Name your connection'}
          </Text>
          {/* P2.1: Show AI model type badge for agent connections */}
          {isAgentConnector && effectiveAgentModelType && (
            <Badge appearance="ghost" color="brand" size="small">
              {effectiveAgentModelType === 'AzureOpenAI' ? 'Azure OpenAI' :
               isFoundryModelType(effectiveAgentModelType) ? 'AI Foundry' :
               effectiveAgentModelType === 'APIMGenAIGateway' ? 'APIM Gateway' :
               effectiveAgentModelType === 'V1ChatCompletionsService' ? 'Chat Completions' :
               effectiveAgentModelType === 'GitHubCopilot' ? 'GitHub Copilot' :
               effectiveAgentModelType}
            </Badge>
          )}
        </div>
        <Text className={styles.connectionNameHelper}>
          {isAgentConnector
            ? 'What should we call this AI connection? This helps identify it in your workflow.'
            : 'This name helps you identify the connection later'}
        </Text>
        <Field
          required
          validationMessage={connectionNameError ?? null}
          validationState={connectionNameError ? 'error' : 'none'}
        >
          <Input
            className={styles.connectionNameInput}
            value={currentConnectionName}
            onChange={(_e, data) => handleConnectionNameChange(data.value)}
            placeholder={suggestedConnectionName}
            data-testid="connection-name-input"
          />
        </Field>
        <div className={styles.connectionNameFooter}>
          {!currentConnectionName && (
            <Text className={styles.connectionNameSuggested}>
              Suggested: {suggestedConnectionName}
            </Text>
          )}
          {currentConnectionName && <span />}
          <Text className={styles.connectionNameCharCount}>
            {currentConnectionName.length}/{CONNECTION_NAME_MAX_LENGTH}
          </Text>
        </div>
      </div>

      {/* Connection details section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <KeyRegular className={styles.sectionIconSymbol} />
          </div>
          <Text className={styles.sectionTitle}>
            {isFoundryModelType(effectiveAgentModelType)
              ? 'Foundry connection'
              : `Details`}
          </Text>
          {useAgentForm && effectiveAgentModelType && getAgentConnectionInfoMessage(effectiveAgentModelType) && (
            <Tooltip
              content={getAgentConnectionInfoMessage(effectiveAgentModelType)}
              relationship="description"
              positioning="after"
              withArrow
            >
              <Button
                className={styles.sectionInfoButton}
                appearance="transparent"
                size="small"
                icon={<Info16Regular className={styles.sectionInfoIcon} />}
                aria-label="More information about this connection"
              />
            </Tooltip>
          )}
        </div>

        {/* Provider type selector — lets users choose between API key and
            GitHub OAuth when creating a connection for coding agents */}
        {showProviderSelector && (
          <div
            className={styles.providerSelector}
            data-testid="provider-type-selector"
            role="group"
            aria-label="Connection provider"
          >
            {codingProviderOptions.map(({ type, label }) => (
              <Button
                key={type}
                appearance={effectiveAgentModelType === type ? 'primary' : 'subtle'}
                size="small"
                aria-pressed={effectiveAgentModelType === type}
                onClick={() => {
                  setSelectedProviderOverride(type);
                  // Reset GHCP OAuth state when switching away
                  if (type !== 'GitHubCopilot') ghcpReset();
                  // Clear form errors from the previous provider
                  setAgentFormErrors({});
                }}
                data-testid={`provider-option-${type}`}
              >
                {label}
              </Button>
            ))}
          </div>
        )}

        <Text className={styles.sectionDescription}>
          {isFoundryModelType(effectiveAgentModelType)
            ? 'Connect to an AI Foundry project to use Foundry agents.'
            : effectiveAgentModelType === 'GitHubCopilot'
            ? 'Sign in with your GitHub account to use Copilot models.'
            : 'Enter the required information to create your connection'}
        </Text>

        {/* Agent connection form */}
        {useAgentForm && effectiveAgentModelType && (
          <div className={styles.fieldGroup}>
            <AgentConnectionForm
              agentModelType={effectiveAgentModelType}
              values={agentFormValues}
              onChange={handleAgentFormChange}
              errors={agentFormErrors}
              hideDisplayName={true}
              onGitHubOAuth={() =>
                // Create the underlying ApiHub OAuth connection under the same
                // normalized name the agent connection's referenceKey uses
                // (connectionName.toLowerCase().replace(/\s+/g, '_'), see
                // handleCreateAgentConnection). The runtime resolves the ApiHub
                // connection by the agent connection's reference name, so the
                // two must match exactly — otherwise the GHCP token lookup fails
                // for any name containing uppercase letters or spaces.
                void ghcpOAuth.connect({
                  connectionName: normalizeConnectionReferenceKey(
                    agentFormValues.displayName || suggestedConnectionName,
                  ),
                })
              }
              isOAuthInProgress={ghcpOAuth.isInProgress}
              isOAuthComplete={ghcpResult?.testResult?.success === true}
              oauthError={ghcpOAuth.error}
              hasNameError={!!connectionNameFormatError || !!duplicateNameError}
            />
          </div>
        )}

        {/* Extra field: azureAgentAIResourceName required for azureagentservice at runtime.
             Only shown when it cannot be derived automatically from the node's endpoint
             (i.e. connectionPropertyOverrides doesn't already have it).
             The OAuth form has no text fields, so we inject this extra input here.
             It is captured in formValues and merged into connectionProperties on save. */}
        {!useAgentForm &&
          connectorId?.toLowerCase().includes('azureagentservice') &&
          !connectionPropertyOverrides?.azureAgentAIResourceName && (
          <div className={styles.fieldGroup}>
            <Field
              label="Azure AI Foundry Project Endpoint"
              required
              hint="Full project endpoint URL, e.g. https://my-resource.services.ai.azure.com/api/projects/my-project"
              validationState={!formValues.azureAgentAIResourceName ? 'none' : 'success'}
            >
              <Input
                value={(formValues.azureAgentAIResourceName as string | undefined) ?? ''}
                onChange={(_e, data) =>
                  setFormValues((prev) => ({ ...prev, azureAgentAIResourceName: data.value }))
                }
                placeholder="https://my-resource.services.ai.azure.com/api/projects/my-project"
                data-testid="azure-agent-resource-name-input"
              />
            </Field>
          </div>
        )}

        {/* Dynamic form from manifest (for non-Agent connectors) */}
        {!useAgentForm && parameterSets.length > 0 && currentParameterSet && (
          <div className={styles.fieldGroup}>
            <DynamicConnectionForm
              parameterSets={parameterSets}
              selectedParameterSet={selectedParameterSet ?? currentParameterSet.name}
              onParameterSetChange={handleParameterSetChange}
              values={formValues}
              onChange={handleFormChange}
              errors={validationErrors}
              onOAuthClick={handleOAuthClick}
              isOAuthInProgress={isOAuthInProgress}
              showAuthTypeSelector={parameterSets.length > 1}
              hideDisplayName={true}
              {...(connectorId?.toLowerCase().includes('azureagentservice')
                ? { excludeKeys: ['azureAgentAIResourceName'] }
                : {})}
            />
          </div>
        )}
      </div>

      {/* Dynamic/OBO Connection Option - P0.4 */}
      {showDynamicConnectionOption && (
        <div className={styles.dynamicConnectionSection}>
          <div className={styles.dynamicConnectionCheckbox}>
            <Checkbox
              checked={createAsDynamic}
              onChange={(_e, data) => setCreateAsDynamic(data.checked === true)}
              label={
                <Text>
                  <PersonRegular className={styles.personIcon} />
                  Create as per-user connection (OBO)
                </Text>
              }
            />
          </div>
          <Text className={styles.dynamicConnectionHelper}>
            Each user will authenticate with their own credentials at runtime
          </Text>
          {createAsDynamic && (
            <MessageBar intent="warning" className={styles.dynamicConnectionWarning}>
              <MessageBarBody>
                Per-user connections require users to authenticate when the workflow runs
              </MessageBarBody>
            </MessageBar>
          )}
        </div>
      )}
    </div>
  );

  // Render creating state
  const renderCreating = () => (
    <div className={styles.loadingContainer}>
      <Spinner size="large" />
      <Text className={styles.loadingText}>{stepLabel}</Text>
      <Text className={styles.loadingSubtext}>
        {isOAuthInProgress && oauthState === 'authenticating'
          ? 'Please complete authorization in the popup window'
          : 'This may take a moment while we verify your credentials'}
      </Text>
    </div>
  );

  // Render auto-create state - P0.3
  const renderAutoCreate = () => (
    <div className={styles.autoCreateContainer}>
      <Spinner size="large" className={styles.autoCreateSpinner} />
      <Text className={styles.autoCreateTitle}>
        Connecting to {connectorName ?? connectorType ?? 'service'}...
      </Text>
      <Text className={styles.autoCreateSubtitle}>
        {oauthState === 'authenticating'
          ? 'Please complete authorization in the popup window'
          : 'Setting up your connection automatically'}
      </Text>
      <Button
        appearance="subtle"
        className={styles.autoCreateCancel}
        onClick={() => {
          // Clear pending timeout so createOAuthConnection doesn't fire after cancel
          clearTimeoutRef(autoCreateTimeoutRef);
          cancelOAuth();
          userCancelledAutoCreateRef.current = true;
          autoCreateInitiatedRef.current = false;
          setAutoCreateTriggered(false);
          setAutoCreateMode('manual');
        }}
      >
        Cancel and configure manually
      </Button>
    </div>
  );

  // Render skeleton loading state while fetching connector metadata
  const renderMetadataLoading = () => (
    <div className={styles.skeletonContainer}>
      {/* Section header skeleton */}
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonHeader}>
          <div className={styles.skeletonIcon} />
          <div className={styles.skeletonTitle} />
        </div>
        <div className={styles.skeletonDescription} />
      </div>

      {/* Field skeleton - mimics the form structure */}
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonField}>
          <div className={styles.skeletonLabel} />
          <div className={styles.skeletonInput} />
        </div>
      </div>

      {/* Button skeleton */}
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonButton} />
      </div>

      {/* Security section skeleton */}
      <div className={mergeClasses(styles.skeletonSection, styles.skeletonSectionMarginTop)}>
        <div className={styles.skeletonHeader}>
          <div className={styles.skeletonIcon} />
          <div className={styles.skeletonTitle} style={{ width: '60px' }} />
        </div>
        <div className={styles.skeletonDescription} style={{ width: '280px' }} />
      </div>
    </div>
  );

  // Render success state
  const renderSuccess = () => (
    <div className={styles.successContainer}>
      <CheckmarkCircleFilled className={styles.successIcon} />
      <Text className={styles.successTitle}>Connection Created!</Text>
      <Text className={styles.successSubtitle}>
        Your {connectorName ?? connectorType ?? 'connection'} is ready to use
      </Text>
      {/* Role assignment feedback for Foundry connections */}
      {isFoundryModelType(effectiveAgentModelType) && roleAssignmentResult && (
        <div className={mergeClasses(
          styles.roleAssignmentFeedback,
          roleAssignmentResult.success ? styles.roleAssignmentSuccess : styles.roleAssignmentWarning,
        )}>
          <Text className={roleAssignmentResult.success ? styles.roleAssignmentSuccessText : styles.roleAssignmentWarningText}>
            {roleAssignmentResult.success
              ? roleAssignmentResult.alreadyAssigned
                ? '✓ Azure AI Developer role already assigned'
                : '✓ Azure AI Developer role granted to managed identity'
              : `⚠ ${roleAssignmentResult.error ?? 'Could not assign permissions automatically. Please assign the "Azure AI Developer" role manually.'}`}
          </Text>
        </div>
      )}
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className={styles.errorContainer}>
      <DismissCircleFilled className={styles.errorIcon} />
      <div className={styles.errorContent}>
        <Text className={styles.errorTitle}>Connection Failed </Text>
        <Text className={styles.errorMessage}>{error ?? oauthError}</Text>
      </div>
    </div>
  );

  // Handle back - cancel OAuth if in progress
  const handleBack = useCallback(() => {
    if (isOAuthInProgress) {
      cancelOAuth();
    }
    onBack();
  }, [isOAuthInProgress, cancelOAuth, onBack]);

  // Show creating state if OAuth is in progress
  const showCreating = step === 'creating' || isOAuthInProgress;

  // Show auto-create UI when OAuth auto-create is triggered - P0.3
  const showAutoCreate = autoCreateTriggered && autoCreateMode === 'oauth' && step === 'form';

  return (
    <div className={styles.container} data-testid="connection-creation-panel">
      {/* Modern Header with progress */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={handleBack}
            className={styles.backButton}
            aria-label="Go back"
          />
          <div className={styles.headerTitleSection}>
            <div className={isFoundryModelType(effectiveAgentModelType) ? styles.headerIconFoundry : styles.headerIcon}>
              {getHeaderIcon()}
            </div>
            <Text className={styles.title}>
              {title ? title : (isFoundryModelType(effectiveAgentModelType) ? 'New Foundry connection' : 'Create connection')}
            </Text>
          </div>
        </div>

        {/* Progress indicator - hidden during auto-create */}
        {!showAutoCreate && (
          <div className={styles.progressSection}>
            <Text className={styles.progressLabel}>
              {stepLabel} · {Math.round(progressValue * 100)}%
            </Text>
            <ProgressBar
              value={progressValue}
              className={styles.progressBar}
            />
          </div>
        )}
      </div>

      {/* Content based on step */}
      {showAutoCreate && renderAutoCreate()}
      {showMetadataLoading && !showAutoCreate && renderMetadataLoading()}
      {step === 'form' && !isOAuthInProgress && !showMetadataLoading && !showAutoCreate && renderForm()}
      {showCreating && !showAutoCreate && renderCreating()}
      {step === 'success' && renderSuccess()}
      {step === 'error' && (
        <>
          {renderError()}
          {renderForm()}
        </>
      )}

      {/* Footer with actions - hidden during auto-create */}
      {(step === 'form' || step === 'error') && !isOAuthInProgress && !showMetadataLoading && !showAutoCreate && (
        <div className={styles.footer}>
          {effectiveAgentModelType !== 'FoundryAgentService' && (
            <div className={styles.securityNote}>
              <ShieldCheckmarkRegular className={styles.securityIcon} />
              <Text size={100}>Credentials are encrypted and stored securely</Text>
            </div>
          )}
          <div className={styles.footerActions}>
            <Button appearance="secondary" onClick={handleBack}>
              Cancel
            </Button>
            {requiresOAuth ? (
              <Button
                appearance="primary"
                onClick={handleOAuthClick}
                disabled={!isValid}
                data-testid="connection-sign-in-button"
              >
                Sign in
              </Button>
            ) : (
              <Button
                appearance="primary"
                onClick={handleCreate}
                disabled={!isValid}
                data-testid="create-connection-submit-button"
              >
                Create connection
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ConnectionCreationPanel.displayName = 'ConnectionCreationPanel';

